import { useEffect, useState, type RefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { votePoll } from '@api-client/api-client';
import { getQuestionLive } from '@api-client/liveRoom';
import { questionLiveKey } from '@lib/cache/polls';
import type { QuestionLiveRead } from '@models/api';
import { getErrorStatus } from '@utils/errorDetection';
import { useLiveAnswer, withLivePicks, type LiveAnswering } from './useLiveAnswer';
import { useLiveQueryPolling } from './useLiveQueryPolling';

/** True while `ref`'s element is on screen (any part of it); false until the first observation. */
export function useOnScreen(ref: RefObject<Element | null>): boolean {
  // Without IntersectionObserver (an old WebView): always on screen.
  const [onScreen, setOnScreen] = useState(() => typeof IntersectionObserver === 'undefined');
  // The viewport is the external system.
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(entries => {
      const last = entries.at(-1);
      if (last) setOnScreen(last.isIntersecting);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [ref]);
  return onScreen;
}

/** An ended poll's card is read again this often: only Back from the results can change it (FE-L3a review). */
export const ENDED_CARD_INTERVAL_MS = 30_000;

/** No longer readable (deleted, left the Space): no read can fix it. */
const unreadable = (error: unknown): boolean => {
  const status = getErrorStatus(error);
  return status === 404 || status === 403;
};

export interface QuestionLive {
  read: QuestionLiveRead | undefined;
  answering: LiveAnswering;
  /** Deleted (404), no longer readable (403) or removed: `read` is the last data, stale; show nothing of it. */
  gone: boolean;
  /**
   * A read has answered with data, or definitively (404/403): until then nothing says whose poll it is. A transient
   * failure (a 500, offline) is not settled: the author's sheet would open empty before `is_author` arrives.
   */
  settled: boolean;
}

/**
 * A member's feed card on a live poll (TECH-867 § API `GET /questions/{id}/live`; CONTRACT § 4.14): the room's
 * state and the reader's `LiveMe`, read every 2 s only while the card is on screen, and answered through
 * TECH-197's vote route with the `position` the card shows (§ 4.3 live branch). E1, E2 and D3b as the sheet
 * (`useLiveAnswer`).
 */
export function useQuestionLive(questionId: string, enabled: boolean): QuestionLive {
  const queryClient = useQueryClient();
  const key = questionLiveKey(questionId);
  const { data, stopped } = useLiveQueryPolling<QuestionLiveRead>({
    queryKey: key,
    read: async signal => (await getQuestionLive(questionId, signal)).data,
    enabled,
    intervalFor: read => (read.state.live_state === 'ended' ? ENDED_CARD_INTERVAL_MS : undefined),
    stopsOn: unreadable,
  });

  const answering = useLiveAnswer({
    scopeId: questionId,
    state: data?.state ?? null,
    me: data?.me ?? null,
    send: async (position, optionIds) => {
      const { data: vote } = await votePoll(questionId, { option_ids: optionIds, position });
      return { joinedSpaceId: vote.joined_space_id };
    },
    writePicks: (position, optionIds) => {
      // A read in flight started before the tap would land after it and wipe the pick: cancel it first.
      void queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData<QuestionLiveRead>(
        key,
        prev => prev && { ...prev, me: withLivePicks(prev.me, position, optionIds) },
      );
    },
    writeMe: me => {
      queryClient.setQueryData<QuestionLiveRead>(key, prev => prev && { ...prev, me });
    },
    writeState: state => {
      queryClient.setQueryData<QuestionLiveRead>(key, prev =>
        prev && prev.state.version <= state.version ? { ...prev, state } : prev,
      );
    },
    rereadMe: () => {
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  return {
    read: data,
    answering,
    gone: stopped || data?.state.removed === true,
    settled: data !== undefined || stopped,
  };
}
